export const getWavDownloadFromProxyWithId = (id, token) => {
  const url = `${process.env.VUE_APP_API_URL}/download?id=${id}`;
  return token ? `${url}&token=${encodeURIComponent(token)}` : url;
};

// Capability-based stream format: WebM/Opus where the browser plays it,
// AAC (ADTS) otherwise. Safari (iOS and macOS) answers "maybe" to
// canPlayType('audio/webm; codecs="opus"') but then can't demux it —
// observed as instant aborts in the server logs — so only "probably"
// counts, and demoteStreamFormat() lets a player that hits an actual
// playback error force AAC for the rest of the session (real playback
// outranks any probe).
let _streamFmt = null;
const streamFormat = () => {
  if (_streamFmt === null) {
    const probe = document.createElement("audio");
    _streamFmt =
      probe.canPlayType('audio/webm; codecs="opus"') === "probably"
        ? "webm"
        : "aac";
  }
  return _streamFmt;
};

export const demoteStreamFormat = () => {
  _streamFmt = "aac";
};

export const getStreamUrlFromProxyWithId = (id, token) => {
  const url = `${process.env.VUE_APP_API_URL}/download/stream/${id}?fmt=${streamFormat()}`;
  return token ? `${url}&token=${encodeURIComponent(token)}` : url;
};

// One synchronized 2-channel mixdown of a whole session (Phase 2) — the simple
// synced preview for logged-out visitors, played from a single <audio> element.
export const getMixdownUrlForSession = (sessionName, token, version, fmtOverride) => {
  // `fmtOverride` (e.g. 'wav') is used by the gapless Web Audio path, which
  // decodes a lossless mix so the loop has no codec-priming gap; the <audio>
  // fallback uses the capability-detected streamFormat() (webm/aac).
  let url = `${process.env.VUE_APP_API_URL}/download/mixdown/${encodeURIComponent(
    sessionName,
  )}?fmt=${fmtOverride || streamFormat()}`;
  // `version` is a signature of the session's loops — it changes the URL when a
  // loop is added/removed so a stale cached mix isn't replayed (the server
  // ignores the param and re-mixes fresh).
  if (version) url += `&v=${encodeURIComponent(version)}`;
  return token ? `${url}&token=${encodeURIComponent(token)}` : url;
};

// Discover's preview: the session's Ogg Opus mixdown (the app's
// feed_preview_player), where the browser plays Ogg Opus (Safari may not);
// null otherwise, and the caller falls back to getMixdownUrlForSession.
export const canPlayOggOpus = () => {
  try {
    return document.createElement("audio").canPlayType('audio/ogg; codecs="opus"') !== "";
  } catch (e) {
    return false;
  }
};

export const mixdownOggUrl = (sessionName, token) => {
  if (!canPlayOggOpus()) return null;
  const url = `${process.env.VUE_APP_API_URL}/download/mixdown/${encodeURIComponent(sessionName)}?fmt=ogg`;
  return token ? `${url}&token=${encodeURIComponent(token)}` : url;
};

// Discover's preview: what the element streams, what it retries, and the
// file audition decodes to loop it seamlessly. The server renders the Ogg
// mixdown one loop long (it expects the client to loop it), so through
// <audio loop> it would seam at every pass; audition plays the element
// first and hands over to the decoded copy of the same file at its next
// wrap (audio/gaplessLoop.js). Where Ogg doesn't play, the webm/aac mix is
// tiled to about two minutes and stays on the element.
export const mixdownPreviewSources = (sessionName, token) => {
  const fallback = getMixdownUrlForSession(sessionName, token);
  const ogg = mixdownOggUrl(sessionName, token);
  return { src: ogg || fallback, fallbackUrl: ogg ? fallback : null, gapless: ogg };
};

// A session's whole-mix waveform (Discover's card, the session's bar item).
export const getSessionWaveformSvgUrl = (sessionName, width = 600, height = 60) =>
  `${process.env.VUE_APP_API_URL}/download/spectrogram/session/${encodeURIComponent(sessionName)}` +
  `?width=${width}&height=${height}&type=waveform`;

// Pre-rendered waveform SVG (server-side, cached). Used as a CSS mask so the
// waveform paints instantly without downloading/decoding the audio.
export const getWaveformSvgUrl = (id, width = 600, height = 64) => {
  return (
    `${process.env.VUE_APP_API_URL}/download/spectrogram/${id}` +
    `?width=${width}&height=${height}&type=waveform`
  );
};

export function waitForLocalStorage(key, cb, timer) {
  if (!localStorage.getItem(key))
    return (timer = setTimeout(waitForLocalStorage.bind(null, key), 100));
  clearTimeout(timer);
  if (typeof cb !== "function") return localStorage.getItem(key);
  return cb(localStorage.getItem(key));
}

// The API returns friends either as a plain array of usernames or, when
// friend metadata is requested, as an object keyed by username with
// { username, friend_count, picture_link } values. Normalize both shapes
// to an array of metadata objects.
export const normalizeFriends = (friends) => {
  if (!friends) return [];
  if (Array.isArray(friends)) {
    return friends.map((friend) =>
      typeof friend === "string"
        ? { username: friend, friend_count: null, picture_link: "" }
        : friend,
    );
  }
  return Object.values(friends);
};

export const sessionViewFilter = (session_raw) => {
  // session_raw.picture = getDownloadLink(session_raw.picture);
  // `private` arrives as a bool (Postgres-hydrated docs) or the string
  // "true" (legacy Redis). Comparing only against "true" turned a boolean
  // true into false, so every live state_update showed private sessions as
  // PUBLIC (and "Make private" would have flipped them public).
  const p = session_raw.private;
  session_raw.private =
    typeof p === "string" ? p.trim().toLowerCase() === "true" : !!p;
  return session_raw;
};

Object.defineProperty(String.prototype, "hashCode", {
  value: function () {
    var hash = 0,
      i,
      chr;
    for (i = 0; i < this.length; i++) {
      chr = this.charCodeAt(i);
      hash = (hash << 5) - hash + chr;
      hash |= 0; // Convert to 32bit integer
    }
    return hash;
  },
});

function hslToHex(h, s, l) {
  h /= 360;
  s /= 100;
  l /= 100;
  let r, g, b;
  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  const toHex = (x) => {
    const hex = Math.round(x * 255).toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export const getColorForString = (stringToHash) => {
  var saturationArray = [35, 50, 65];
  var lightnessArray = [35, 50, 65];
  const hashResult = stringToHash.hashCode();
  let hue = Math.abs(hashResult % 359.0);
  let saturationIndex = ((hue * 180) % (saturationArray.length - 1)) + 1;
  let lightnessIndex = ((hue * 180) % (saturationArray.length - 1)) + 1;
  return hslToHex(
    hue,
    saturationArray[saturationIndex],
    lightnessArray[lightnessIndex],
  );
};

// Show the server share path in the address bar while viewing a session or
// profile, e.g. /s/<name>#/session/<name>. The hash route alone never
// unfurls in chat apps (a link previewer never sees the fragment, so it gets
// the homepage card); /s/ and /u/ serve OpenGraph pages, and on reload they
// 302 straight back into this hash route. Same-origin deployments only —
// elsewhere (local serve) those paths don't reach the share routes.
export function showShareablePath(prefix, name, hashPath) {
  if (!name) return;
  try {
    const api = new URL(process.env.VUE_APP_API_URL || "", window.location.href);
    if (api.origin !== window.location.origin) return;
  } catch (e) {
    return;
  }
  const target = `/${prefix}/${encodeURIComponent(name)}${hashPath}`;
  if (window.location.pathname + window.location.hash === target) return;
  // Keep the router's history state so back/forward still work.
  window.history.replaceState(window.history.state, "", target);
}
